/**
 * CameraPermissionError — Écran affiché si permissions caméra refusées
 *
 * Plein écran, fond noir nuit.
 * Logo Lumène centré en haut.
 * Icône caméra barrée en cuivre patiné.
 * Message d'explication + CTA "Ouvrir les paramètres".
 *
 * Sur iOS Safari et Android Chrome, il n'existe pas d'API web
 * permettant d'ouvrir directement les paramètres système.
 * Le CTA affiche donc des instructions contextuelles selon l'OS détecté.
 */

import React from 'react'
import { Box, Text } from '@chakra-ui/react'
import { motion, useReducedMotion } from 'framer-motion'
import LumeneLogo from './LumeneLogo'

// ── Détection OS rudimentaire ────────────────────────────────────────────────
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent)

// ── Icône caméra barrée ──────────────────────────────────────────────────────
const CameraOffIcon = () => (
  <svg
    width={52} height={52}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#B08D5B"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1={1} y1={1} x2={23} y2={23}/>
    <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34"/>
    <circle cx={12} cy={13} r={4}/>
  </svg>
)

// ── Icône flèche (instructions iOS) ─────────────────────────────────────────
const ArrowIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
       stroke="#B08D5B" strokeWidth={2} strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true">
    <line x1={5} y1={12} x2={19} y2={12}/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
)

// ── Composant principal ──────────────────────────────────────────────────────
const CameraPermissionError = () => {
  const ios = isIOS()
  const prefersReduced = useReducedMotion()

  const instructions = ios
    ? 'Réglages → Safari → Caméra → Autoriser'
    : 'Paramètres du navigateur → Autorisations → Caméra'

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: prefersReduced ? 0.15 : 0.3, ease: [0.0, 0.0, 0.2, 1] }}
      style={{
        position:       'fixed',
        inset:          0,
        background:     '#1A1D24',
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        '32px 28px',
        textAlign:      'center',
      }}
    >
      {/* Logo centré en haut */}
      <Box
        position="absolute"
        top="calc(env(safe-area-inset-top, 0px) + 24px)"
        left="50%"
        transform="translateX(-50%)"
      >
        <LumeneLogo size="sm" />
      </Box>

      {/* Icône caméra barrée */}
      <motion.div
        initial={prefersReduced ? { opacity: 0 } : { scale: 0.8, opacity: 0 }}
        animate={prefersReduced ? { opacity: 1 } : { scale: 1, opacity: 1 }}
        transition={{ delay: prefersReduced ? 0 : 0.1, duration: prefersReduced ? 0.15 : 0.3, ease: [0.34, 1.56, 0.64, 1] }}
        style={{ marginBottom: '24px' }}
      >
        <CameraOffIcon />
      </motion.div>

      {/* Titre */}
      <Text
        as="h1"
        fontFamily="'Atkinson Hyperlegible', system-ui, sans-serif"
        fontSize="22px"
        fontWeight={700}
        color="#F8F4EC"
        mb={3}
        lineHeight={1.2}
      >
        Accès à la caméra nécessaire
      </Text>

      {/* Message principal */}
      <Text
        fontFamily="'Atkinson Hyperlegible', system-ui, sans-serif"
        fontSize="16px"
        color="#F8F4EC"
        opacity={0.8}
        lineHeight={1.7}
        mb={4}
        maxW="320px"
      >
        Lumène a besoin de la caméra pour identifier les œuvres.
        Autorisez l'accès puis rechargez la page.
      </Text>

      {/* Instructions contextuelles */}
      <Box
        bg="rgba(176,141,91,0.12)"
        border="1px solid rgba(176,141,91,0.3)"
        borderRadius="8px"
        px={4} py={3}
        mb={8}
        maxW="300px"
      >
        <Box display="flex" alignItems="center" gap={2} justifyContent="center">
          <ArrowIcon />
          <Text
            fontFamily="'Atkinson Hyperlegible', system-ui, sans-serif"
            fontSize="13px"
            color="#B08D5B"
            letterSpacing="0.3px"
          >
            {instructions}
          </Text>
        </Box>
      </Box>

      {/* CTA — rechargement page */}
      <button
        onClick={() => window.location.reload()}
        aria-label="Recharger la page après avoir autorisé la caméra"
        style={{
          background:    '#B08D5B',
          color:         '#1A1D24',
          border:        'none',
          padding:       '14px 32px',
          borderRadius:  '4px',
          fontFamily:    "'Atkinson Hyperlegible', system-ui, sans-serif",
          fontSize:      '13px',
          fontWeight:    700,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          cursor:        'pointer',
          minHeight:     '48px',
          minWidth:      '220px',
        }}
      >
        Recharger la page
      </button>

    </motion.div>
  )
}

export default CameraPermissionError
